import React, { Component, ReactNode } from 'react';
import { observer } from 'mobx-react';
import autoBindMethods from 'class-autobind-decorator';
import cx from 'classnames';
import { formatDate, formatDateTime } from '@mighty-justice/utils';

import { ILoanAccountActivity } from '../../../interfaces';
import { getInitials } from '../../../utils/util';
import { Icon } from '../../common';

interface IProps {
  activity: ILoanAccountActivity;
  activity_body: ReactNode;
  // Buttons the entry offers on its footer, which today only a note has
  actions?: ReactNode;
  dot?: string;
  icon?: string;
}

/**
 * The shell every loan account activity renders into: a dot, the body its own component builds,
 * and a footer crediting whoever performed it.
 *
 * c.f. CaseActivity, which this follows minus the note editing case activities support.
 */
@autoBindMethods
@observer
class LoanAccountActivity extends Component<IProps> {
  public static defaultProps: Partial<IProps> = {
    dot: 'name',
  };

  private get performedByName () {
    const { performed_by } = this.props.activity;

    if (!performed_by || !performed_by.first_name) { return null; }

    return `${performed_by.first_name}${performed_by.last_name ? ` ${performed_by.last_name}` : ''}`;
  }

  private get performedByInitials () {
    const { performed_by } = this.props.activity;

    if (!performed_by || !performed_by.first_name || !performed_by.last_name) { return null; }

    return getInitials(performed_by.first_name, performed_by.last_name);
  }

  private renderDot () {
    const { dot, icon } = this.props;

    if (dot === 'name') {
      return <div className='dot name'>{this.performedByInitials}</div>;
    }

    if (dot === 'icon') {
      return <div className='dot icon'>{icon && <Icon type={icon} />}</div>;
    }

    // dot === small
    return <div className='dot small' />;
  }

  public render () {
    const { actions, activity, activity_body } = this.props
      , { created_at, deleted_at, type } = activity
      , isDeleted = !!deleted_at
      ;

    return (
      <div className={cx('activity-item', { deleted: isDeleted })} data-type={type}>
        {this.renderDot()}

        <div className='body'>{activity_body}</div>

        <div className='footer'>
          <span className='date'>{formatDateTime(created_at)}</span>
          {this.performedByName && <span className='author'>&nbsp;{this.performedByName}</span>}
          {actions}
          {isDeleted && <span className='deleted-at'>Deleted at {formatDate(deleted_at)}</span>}
        </div>
      </div>
    );
  }
}

export default LoanAccountActivity;
